import React, { useState, useEffect } from 'react';
import { Modal, Select, Input, Button, Typography, Checkbox, Form, Tabs, Alert, message } from 'antd';
import { MessageSquare, X, Send, Globe, Image as ImageIcon } from 'lucide-react';
import dialerService from '../services/dialerService';
import { getSession } from '../../../getSession';

const { Text } = Typography;
const { TextArea } = Input;

const SMSModalV2 = ({ isOpen, onClose, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [senderIds, setSenderIds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [charCount, setCharCount] = useState(0);
  const [activeTab, setActiveTab] = useState('indian');

  useEffect(() => {
    if (isOpen && record) {
      setLeadMobileNumbers([]);
      fetchInitialData();
      fetchLeadData();
      form.resetFields();
    }
  }, [isOpen, record, form]);

  const fetchLeadData = async () => {
    // Immediate fallback from record
    const fallbackNos = [];
    const mainNo = record.ScheduleMobileNo || record.MobileNo;
    if (mainNo) {
      fallbackNos.push({ label: mainNo, value: mainNo });
      setLeadMobileNumbers(fallbackNos);
      form.setFieldsValue({ selectedMobiles: [mainNo] });
    }

    try {
      const lead = await dialerService.getLeadById(record.Id);
      if (lead) {
        const mobileNos = [];
        if (lead.MobileNo) mobileNos.push({ label: `${lead.CountryCode || ''} ${lead.MobileNo}`, value: lead.MobileNo });
        if (lead.MobileNo1) mobileNos.push({ label: `${lead.CountryCode1 || ''} ${lead.MobileNo1}`, value: lead.MobileNo1 });
        if (lead.MobileNo2) mobileNos.push({ label: `${lead.CountryCode2 || ''} ${lead.MobileNo2}`, value: lead.MobileNo2 });

        if (mobileNos.length > 0) {
          setLeadMobileNumbers(mobileNos);
          form.setFieldsValue({
            selectedMobiles: mobileNos.map(m => m.value)
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch lead data:', error);
    }
  };

  const [leadMobileNumbers, setLeadMobileNumbers] = useState([]);

  const fetchInitialData = async () => {
    try {
      const [accRes, senderRes, placeRes] = await Promise.all([
        dialerService.getSmsAccounts(),
        dialerService.getSmsSenderIds(),
        dialerService.getEntityPlaceholders()
      ]);
      if (accRes) setAccounts(accRes.map(a => ({ label: a.Text, value: String(a.Code || a.Value || a.Id) })));
      if (senderRes) setSenderIds(senderRes.map(s => ({ label: s.Text, value: String(s.Code || s.Value || s.Id) })));
      if (placeRes) setPlaceholders(placeRes.map(p => ({ label: p.ExposedName, value: String(p.ExposedName) })));
    } catch (error) {
      console.error('Failed to fetch SMS data:', error);
    }
  };

  const handleSenderIdChange = async (value) => {
    try {
      const res = await dialerService.getSmsTemplates(value);
      if (res) setTemplates(res.map(t => ({ label: t.Text, value: t.Code })));
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleTemplateChange = async (value) => {
    try {
      const res = await dialerService.getDLTTemplateText(value);
      const data = res; // Already parsed by service
      if (data && data.length > 0) {
        const template = data[0];
        form.setFieldsValue({
          message: template.TemplateText,
          isUnicode: template.IsUnicode === 'True'
        });
      }
    } catch (error) {
      console.error('Failed to fetch template text:', error);
    }
  };

  const handleSend = async (values) => {
    setLoading(true);
    try {
      const payload = {
        EnquiryLeadId: record.Id,
        EntityType: record.EntityName || 'Lead',
        MobileNo: values.selectedMobiles[0] || '',
        MobileNo1: values.selectedMobiles[1] || '',
        MobileNo2: values.selectedMobiles[2] || '',
      };

      if (activeTab === 'indian') {
        const smsData = {
          ...payload,
          Account: values.account,
          SenderID: values.senderId,
          SenderName: senderIds.find(s => s.value === values.senderId)?.label,
          Template: values.template,
          Message: values.message,
          IsUnicode: !!values.isUnicode,
          SmsType: 'Compose',
          UserId: getSession().userId
        };
        const res = await dialerService.sendSms(smsData);
        if (res.Details) {
          message.success(res.Details);
        } else {
          message.success('SMS sent successfully');
        }
      } else {
        const smsData = {
          ...payload,
          SenderID: values.intSenderId,
          Message: values.intMessage,
          IsUnicode: false, // Default for international
          UrlTack: !!values.urlTrack,
          UrlTackText: values.targetUrl,
          UserId: getSession().userId
        };
        const res = await dialerService.sendInternationalSms(smsData);
        if (res.Details) {
          message.success(res.Details);
        } else {
          message.success('International SMS sent successfully');
        }
      }
      onClose();
    } catch (error) {
      console.error('Failed to send SMS:', error);
      message.error('Failed to send SMS. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  const NormalSMSTab = (
    <div className="space-y-4 pt-4">
      <Form.Item
        name="selectedMobiles"
        label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> CHOOSE MOBILE NUMBERS</span>}
        rules={[{ required: true, message: 'Select at least one number' }]}
        className="mb-6"
      >
        <Checkbox.Group className="w-full">
          <div className="grid grid-cols-1 gap-2">
            {leadMobileNumbers.map(m => (
              <div key={m.value} className="flex items-center p-3 bg-slate-50 border border-slate-100 rounded-xl hover:bg-emerald-50 hover:border-emerald-200 transition-colors cursor-pointer group">
                <Checkbox value={m.value} className="text-slate-700 font-bold w-full">{m.label}</Checkbox>
              </div>
            ))}
          </div>
        </Checkbox.Group>
      </Form.Item>

      <Form.Item
        name="account"
        label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Account</span>}
        rules={[{ required: true, message: 'Select account' }]}
      >
        <Select placeholder="Select Account" options={accounts} className="h-11" getPopupContainer={trigger => trigger.parentNode} />
      </Form.Item>

      <Form.Item
        name="senderId"
        label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Sender ID</span>}
        rules={[{ required: true, message: 'Select SenderID' }]}
      >
        <Select placeholder="Select SenderID" options={senderIds} onChange={handleSenderIdChange} className="h-11" getPopupContainer={trigger => trigger.parentNode} />
      </Form.Item>

      <Form.Item
        name="template"
        label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Template</span>}
        rules={[{ required: true, message: 'Select template' }]}
      >
        <Select
          placeholder="Select Template"
          options={templates}
          className="h-11"
          onChange={handleTemplateChange}
          getPopupContainer={trigger => trigger.parentNode}
        />
      </Form.Item>

      <Form.Item
        name="message"
        label={<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Message</span>}
      >
        <TextArea rows={4} placeholder="SMS message content..." className="rounded-xl" />
      </Form.Item>

      <Form.Item name="isUnicode" valuePropName="checked">
        <Checkbox className="text-slate-600 font-medium">Is Unicode</Checkbox>
      </Form.Item>
    </div>
  );

  const InternationalSMSTab = (
    <div className="space-y-4 pt-4">
      <Alert
        message="International SMS Notice"
        description="Ensure your authentication key is generated. Wallet balance must be sufficient."
        type="info"
        showIcon
        className="rounded-xl"
      />

      <Form.Item
        name="intSenderId"
        label={<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sender ID</span>}
        rules={[{ required: true, message: 'Enter SenderID' }]}
      >
        <Input placeholder="Enter SenderID" className="h-11 rounded-xl" maxLength={11} />
      </Form.Item>

      <div className="flex items-center gap-2">
        <Select
          placeholder="Choose Placeholder"
          options={placeholders}
          className="flex-1 h-11"
          onChange={(val) => {
            const currentMsg = form.getFieldValue('intMessage') || '';
            form.setFieldsValue({ intMessage: currentMsg + ` #${val}# ` });
          }}
        />
        <ImageIcon size={20} className="text-slate-400" />
      </div>

      <Form.Item
        name="intMessage"
        label={<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Message</span>}
        rules={[{ required: true, message: 'Enter message' }]}
      >
        <div className="relative">
          <TextArea
            rows={5}
            placeholder="Enter message content..."
            className="rounded-xl"
            onChange={(e) => setCharCount(e.target.value.length)}
          />
          <div className="absolute bottom-2 right-3 text-[10px] font-bold text-slate-400 bg-white/80 px-2 py-0.5 rounded-full border border-slate-100 shadow-sm">
            {charCount} / 2000
          </div>
        </div>
      </Form.Item>

      <div className="flex items-center justify-between">
        <Form.Item name="urlTrack" valuePropName="checked" className="mb-0">
          <Checkbox className="text-slate-600 font-medium">URL Track</Checkbox>
        </Form.Item>
      </div>

      <Form.Item
        name="targetUrl"
        label={<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Target URL</span>}
        hidden={!form.getFieldValue('urlTrack')}
      >
        <Input placeholder="Enter URL to track" className="h-11 rounded-xl" />
      </Form.Item>
    </div>
  );

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <MessageSquare size={20} className="text-emerald-600" />
          <span className="text-lg font-bold text-slate-800">Send SMS</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSend}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="premium-tabs"
          items={[
            {
              key: 'indian',
              label: (
                <span className="flex items-center gap-2 px-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Indian SMS
                </span>
              ),
              children: NormalSMSTab,
            },
            {
              key: 'intl',
              label: (
                <span className="flex items-center gap-2 px-2">
                  <Globe size={14} className="text-indigo-500" />
                  Non-Indian SMS
                </span>
              ),
              children: InternationalSMSTab,
            },
          ]}
        />

        <div className="flex justify-end gap-3 mt-8 border-t border-slate-100 pt-6">
          <Button onClick={onClose} className="rounded-xl font-bold h-11 px-6">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<Send size={16} />}
            className="rounded-xl font-bold h-11 px-10 bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-200 border-none flex items-center gap-2"
          >
            Send SMS
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default SMSModalV2;
